import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "antd";
import PropTypes from "prop-types";
import { toast } from "react-hot-toast";
import { RiCheckboxCircleLine, RiFileCopyLine, RiLink, RiShareForwardLine, RiWhatsappLine } from "react-icons/ri";
import { apiCall } from "utils/apiClient";

const DEFAULT_EXPIRES_IN_DAYS = 30;
const DEFAULT_FRONTEND_URL = String(process.env.REACT_APP_FRONTEND_URL || "https://seeb.in").trim();

const trimTrailingSlash = (value) => String(value || "").trim().replace(/\/+$/, "");

const normalizeLinkPayload = (result) => {
  const payload = result?.data && typeof result.data === "object" ? result.data : result || {};

  return {
    token: payload.token || "",
    code: payload.code || "",
    review_url: payload.review_url || payload.reviewUrl || "",
    short_url: payload.short_url || payload.shortUrl || "",
    deep_link_url: payload.deep_link_url || payload.deepLinkUrl || "",
    preferred_share_url: payload.preferred_share_url || payload.preferredShareUrl || "",
  };
};

const getLinkPayload = (value) => (value?.data && typeof value.data === "object" ? value.data : value || {});

const parseDate = (value) => {
  if (!value) return null;

  const parsed = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isUsableExistingLink = (link) => {
  const payload = getLinkPayload(link);
  const expiresAt = parseDate(payload.expires_at || payload.expiresAt);
  const isActive =
    payload.is_active === true ||
    payload.is_active === 1 ||
    String(payload.is_active).toLowerCase() === "true" ||
    String(payload.status || "").toLowerCase() === "active";

  return isActive && !payload.revoked_at && (!expiresAt || expiresAt.getTime() > Date.now());
};

const getLatestExistingLink = (links) =>
  [...links]
    .filter(isUsableExistingLink)
    .sort((left, right) => {
      const leftPayload = getLinkPayload(left);
      const rightPayload = getLinkPayload(right);
      const leftTime =
        parseDate(leftPayload.updated_at || leftPayload.created_at || leftPayload.updatedAt || leftPayload.createdAt)?.getTime() || 0;
      const rightTime =
        parseDate(rightPayload.updated_at || rightPayload.created_at || rightPayload.updatedAt || rightPayload.createdAt)?.getTime() || 0;

      if (rightTime !== leftTime) {
        return rightTime - leftTime;
      }

      return Number(rightPayload.id || right?.id || 0) - Number(leftPayload.id || left?.id || 0);
    })[0] || null;

const normalizeShareLinksResponse = (result) => {
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result?.data?.data)) return result.data.data;
  return [];
};

const copyText = async (text) => {
  if (!text) return false;

  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  const input = document.createElement("textarea");
  input.value = text;
  input.setAttribute("readonly", "readonly");
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.appendChild(input);
  input.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(input);

  return copied;
};

const buildWhatsappUrl = (phoneNumber, shareUrl, bookingCode) => {
  if (!shareUrl) return "";

  const rawDigits = String(phoneNumber || "").replace(/\D/g, "");
  const digits =
    rawDigits.length === 10
      ? `91${rawDigits}`
      : rawDigits.startsWith("0") && rawDigits.length === 11
      ? `91${rawDigits.slice(1)}`
      : rawDigits;
  const message = [
    "Hi, please share your review for your completed booking.",
    bookingCode ? `Booking: ${bookingCode}` : "",
    shareUrl,
  ]
    .filter(Boolean)
    .join("\n");

  return `https://wa.me/${digits || ""}?text=${encodeURIComponent(message)}`;
};

const LinkRow = ({ label, value, onCopy }) => (
  <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
    <div className="mb-1 flex items-center justify-between gap-3">
      <span className="text-[11px] font-semibold uppercase text-gray-500">{label}</span>
      <button
        type="button"
        onClick={() => onCopy(value)}
        disabled={!value}
        className="inline-flex h-7 items-center gap-1 rounded border border-gray-300 bg-white px-2 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <RiFileCopyLine size={14} />
        Copy
      </button>
    </div>
    <p className="break-all text-sm font-medium text-gray-800">{value || "-"}</p>
  </div>
);

LinkRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onCopy: PropTypes.func.isRequired,
};

LinkRow.defaultProps = {
  value: "",
};

const ReviewShareLinkModal = ({ open, onClose, booking }) => {
  const [expiresInDays, setExpiresInDays] = useState(DEFAULT_EXPIRES_IN_DAYS);
  const [frontendUrl, setFrontendUrl] = useState(DEFAULT_FRONTEND_URL);
  const [loading, setLoading] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [linkSource, setLinkSource] = useState("");
  const [links, setLinks] = useState(null);

  const isCompleted = String(booking?.status || "").toLowerCase() === "completed";
  const reviewUrl = links?.review_url || "";
  const shareUrl = links?.preferred_share_url || links?.short_url || links?.deep_link_url || reviewUrl;

  const whatsappUrl = useMemo(
    () => buildWhatsappUrl(booking?.mobile_no, shareUrl, booking?.booking_code),
    [booking?.booking_code, booking?.mobile_no, shareUrl]
  );

  const loadExistingShareLink = async () => {
    if (!booking?.id || !isCompleted) return null;

    setCheckingExisting(true);

    try {
      const result = await apiCall({
        endpoint: `reviews/admin/booking/${booking.id}/share-links`,
        method: "GET",
      });
      console.log("Existing share links response:", result);

      if (result?.status !== 200 && result?.success !== true) {
        return null;
      }

      const existingLink = getLatestExistingLink(normalizeShareLinksResponse(result));

      if (existingLink) {
        setLinks(normalizeLinkPayload(existingLink));
        setLinkSource("existing");

        return existingLink;
      }
    } catch (error) {
      console.error("Error fetching existing review share links:", error);
    } finally {
      setCheckingExisting(false);
    }

    return null;
  };

  useEffect(() => {
    if (open) {
      setLinks(null);
      setLinkSource("");
      setExpiresInDays(DEFAULT_EXPIRES_IN_DAYS);
      setFrontendUrl(DEFAULT_FRONTEND_URL);
      loadExistingShareLink();
    }
  }, [open, booking?.id, isCompleted]);

  const handleCreateLink = async () => {
    if (!booking?.id) {
      toast.error("Booking ID is missing");
      return;
    }

    if (!isCompleted) {
      toast.error("Review links can be created only for completed bookings");
      return;
    }

    const payload = {
      expires_in_days: Number(expiresInDays) || DEFAULT_EXPIRES_IN_DAYS,
    };
    const cleanedFrontendUrl = trimTrailingSlash(frontendUrl);

    if (cleanedFrontendUrl) {
      payload.frontend_url = cleanedFrontendUrl;
    } else {
      toast.error("Frontend URL is required");
      return;
    }

    setLoading(true);

    try {
      const result = await apiCall({
        endpoint: `reviews/admin/booking/${booking.id}/share-link`,
        method: "POST",
        data: payload,
      });

      if (result?.status === 200 || result?.status === 201 || result?.success === true) {
        const nextLinks = normalizeLinkPayload(result);
        setLinks(nextLinks);
        setLinkSource("new");
        toast.success(result?.message || "Review link created");
      } else {
        toast.error(result?.message || "Failed to create review link");
      }
    } catch (error) {
      console.error("Error creating review share link:", error);
      toast.error("Failed to create review link");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text) => {
    try {
      const copied = await copyText(text);
      if (copied) {
        toast.success("Link copied");
      } else {
        toast.error("Unable to copy link");
      }
    } catch (error) {
      toast.error("Unable to copy link");
    }
  };

  const handleNativeShare = async () => {
    if (!shareUrl) return;

    if (!navigator?.share) {
      await handleCopy(shareUrl);
      return;
    }

    try {
      await navigator.share({
        title: "Booking Review",
        text: "Please share your review for your completed booking.",
        url: shareUrl,
      });
    } catch (error) {
      if (error?.name !== "AbortError") {
        toast.error("Unable to open share options");
      }
    }
  };

  const handleClose = () => {
    setLinks(null);
    setLoading(false);
    setCheckingExisting(false);
    setLinkSource("");
    setExpiresInDays(DEFAULT_EXPIRES_IN_DAYS);
    setFrontendUrl(DEFAULT_FRONTEND_URL);
    onClose();
  };

  return (
    <Modal
      title="Create Review Link"
      open={open}
      onCancel={handleClose}
      footer={null}
      width={680}
      zIndex={9999}
      destroyOnClose
    >
      <div className="space-y-4">
        <div className="rounded-md border border-gray-100 bg-gray-50 p-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <p className="text-[11px] font-semibold uppercase text-gray-500">Booking</p>
              <p className="mt-1 text-sm font-semibold text-gray-800">{booking?.booking_code || booking?.id || "-"}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase text-gray-500">Customer</p>
              <p className="mt-1 text-sm font-semibold text-gray-800">{booking?.user_name || "-"}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase text-gray-500">Status</p>
              <span
                className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                  isCompleted ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"
                }`}
              >
                {booking?.status || "-"}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[150px_1fr]">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-gray-600">Expires In Days</span>
            <input
              type="number"
              min="1"
              value={expiresInDays}
              onChange={(event) => setExpiresInDays(event.target.value)}
              className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-gray-600">Frontend URL</span>
            <input
              type="url"
              value={frontendUrl}
              onChange={(event) => setFrontendUrl(event.target.value)}
              placeholder="https://your-frontend.com"
              className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm text-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            />
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleCreateLink}
            disabled={loading || !isCompleted}
            className="inline-flex h-9 items-center gap-2 rounded-md bg-blue-600 px-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RiLink size={17} />
            {loading ? "Creating..." : shareUrl ? "Create New Link" : "Create Link"}
          </button>
          {shareUrl && (
            <>
              <button
                type="button"
                onClick={() => handleCopy(shareUrl)}
                className="inline-flex h-9 items-center gap-2 rounded-md border border-gray-300 px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                <RiFileCopyLine size={17} />
                Copy Share Link
              </button>
              <button
                type="button"
                onClick={handleNativeShare}
                className="inline-flex h-9 items-center gap-2 rounded-md border border-gray-300 px-3 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                <RiShareForwardLine size={17} />
                Share
              </button>
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-9 items-center gap-2 rounded-md bg-green-600 px-3 text-sm font-semibold text-white hover:bg-green-700"
              >
                <RiWhatsappLine size={17} />
                WhatsApp
              </a>
            </>
          )}
        </div>

        {!isCompleted && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Complete this booking before creating a review link.
          </div>
        )}

        {links && (
          <div className="space-y-3 border-t border-gray-100 pt-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-green-700">
              <RiCheckboxCircleLine size={18} />
              {linkSource === "existing" ? "Existing active review link is ready to share" : "Review link is ready to share"}
            </div>
            <LinkRow label="Share URL" value={links.preferred_share_url} onCopy={handleCopy} />
            {/* <LinkRow label="Deep Link URL" value={links.deep_link_url} onCopy={handleCopy} />
            <LinkRow label="Short URL" value={links.short_url} onCopy={handleCopy} />
            <LinkRow label="Review URL" value={links.review_url} onCopy={handleCopy} />
            <LinkRow label="Code" value={links.code} onCopy={handleCopy} />
            <LinkRow label="Token" value={links.token} onCopy={handleCopy} /> */}
          </div>
        )}
      </div>
    </Modal>
  );
};

ReviewShareLinkModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  booking: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    booking_code: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    mobile_no: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    status: PropTypes.string,
    user_name: PropTypes.string,
  }).isRequired,
};

export default ReviewShareLinkModal;
